import { useEffect, useState } from 'react';
import { toast } from 'sonner';
import { useLanguage } from '@/contexts/LanguageContext';
import { useAuth } from '@/contexts/AuthContext';
import { addLink, updateLink, deleteLink } from '@/lib/firebase/queries/links';
import { addDonation, updateDonation, deleteDonation, getLinkDonations } from '@/lib/firebase/queries/donations';
import { useTreatmentCategories } from '@/lib/firebase/queries/treatmentCategories';
import {
  canReduceReceived,
  computeAggregateDonationWrite,
  findAggregateDonation,
  formatDonationDate,
  noonLocalDate,
  toDateInput,
  type ComputedLink,
} from '@/lib/linkFinancials';
import type { DonationTransaction, Link, TreatmentSupportRequest } from '@/types';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Badge } from '@/components/ui/badge';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
} from '@/components/ui/dropdown-menu';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { CategorySelect } from '@/components/shared/CategorySelect';
import { SaudiRiyalIcon } from '@/components/ui/saudi-riyal-icon';
import { MoreHorizontal, Pencil, Trash2, Plus, Loader2 } from 'lucide-react';

interface UseMarketerProjectDialogsArgs {
  links: Link[];
  requests: TreatmentSupportRequest[];
  updateRequest: (id: string, data: Partial<TreatmentSupportRequest>) => Promise<void>;
  marketingX: number;
  marketingY: number;
  /** Refetches every link's donation total (server-side aggregation) — call after any write that
   *  touches a `donations` document, since those writes don't retrigger useLinks(). */
  refreshLinkTotals: () => Promise<void>;
}

const emptyAddLinkForm = {
  platform: '' as '' | 'jood' | 'ehsan' | 'tabarru' | 'shefaa',
  caseNumber: '',
  url: '',
  actualReceived: '',
  treatmentCategoryId: '',
  linkTitle: '',
};

const emptyEditLinkForm = { url: '', actualReceived: '', treatmentCategoryId: '', linkTitle: '' };

const emptyDonationForm = { id: '', amount: '', date: '', donorName: '', donorPhone: '', paymentMethod: '' };

/**
 * Mutation surface for the marketer projects view: add/edit/delete a project (`links` doc)
 * and manage that project's donation transactions (`donations` docs).
 *
 * Behavioural contract originally mirrored from CoordinatorDashboard.tsx's coordinator
 * project-mutation handlers (handleAddLinkCoord, handleSaveEditLinkCoord, handleDeleteLinkCoord,
 * handleSaveDonation, handleDeleteDonation). Those handlers were deleted from
 * CoordinatorDashboard.tsx when the coordinator's projects view was narrowed to read-mostly;
 * this dialog module is now the only place that logic lives.
 *
 * The one permitted case write on the whole marketer surface: handleDeleteProject strips this
 * project's entries from every affected case's `donationEntries` via `updateRequest` before
 * deleting the link. Everything else here only ever touches `links`/`donations`.
 */
export function useMarketerProjectDialogs({
  links,
  requests,
  updateRequest,
  marketingX,
  marketingY,
  refreshLinkTotals,
}: UseMarketerProjectDialogsArgs) {
  const { t, dir } = useLanguage();
  const { currentUser } = useAuth();
  const { categories } = useTreatmentCategories();
  const activeCategories = categories.filter((c) => c.isActive);

  const [isSubmitting, setIsSubmitting] = useState(false);

  // Add project
  const [showAddLink, setShowAddLink] = useState(false);
  const [addLinkForm, setAddLinkForm] = useState(emptyAddLinkForm);
  const resetAddLinkForm = () => setAddLinkForm(emptyAddLinkForm);

  // Edit project
  const [editingLink, setEditingLink] = useState<ComputedLink | null>(null);
  const [editLinkForm, setEditLinkForm] = useState(emptyEditLinkForm);

  const startEditLink = (link: ComputedLink) => {
    setEditingLink(link);
    setEditLinkForm({
      url: link.url || '',
      actualReceived: link.actualReceived ? String(link.actualReceived) : '',
      treatmentCategoryId: link.treatmentCategoryId || '',
      linkTitle: link.linkTitle || '',
    });
  };

  // Manage donations
  const [managingDonationsLink, setManagingDonationsLink] = useState<ComputedLink | null>(null);
  const [showDonationForm, setShowDonationForm] = useState(false);
  const [donationForm, setDonationForm] = useState(emptyDonationForm);

  // One link's donation rows, fetched on demand only while the edit dialog or manage-donations
  // dialog is open for it (never up front) — replaces the full `donations` array this hook used
  // to receive as a prop. Single-field `where('linkId','==',id)` query, no composite index.
  const [scopedDonations, setScopedDonations] = useState<DonationTransaction[]>([]);
  const [scopedLoading, setScopedLoading] = useState(false);
  const scopedLinkId = managingDonationsLink?.id || editingLink?.id;

  useEffect(() => {
    if (!scopedLinkId) { setScopedDonations([]); setScopedLoading(false); return; }
    let cancelled = false;
    setScopedLoading(true);
    getLinkDonations(scopedLinkId).then((rows) => {
      if (cancelled) return;
      setScopedDonations(rows);
      setScopedLoading(false);
    });
    return () => { cancelled = true; };
  }, [scopedLinkId]);

  const refreshScopedDonations = async () => {
    if (!scopedLinkId) return;
    setScopedDonations(await getLinkDonations(scopedLinkId));
  };

  const startAddDonation = () => {
    setDonationForm({ ...emptyDonationForm, date: toDateInput(new Date()) });
    setShowDonationForm(true);
  };
  const startEditDonation = (d: DonationTransaction) => {
    setDonationForm({
      id: d.id,
      amount: String(d.amount ?? ''),
      // Invalid date (dateless donation, see donations.ts mapper) must not reach the input as
      // 'NaN-NaN-NaN' — prefill empty. handleSaveDonation already requires donationForm.date to
      // be non-empty, so saving without fixing it is blocked by the existing required-field check.
      date: d.date && !isNaN(d.date.getTime()) ? toDateInput(d.date) : '',
      donorName: d.donorName || '',
      donorPhone: d.donorPhone || '',
      paymentMethod: d.paymentMethod || '',
    });
    setShowDonationForm(true);
  };

  // ===== Handlers =====

  const handleAddProject = async () => {
    if (!addLinkForm.platform || !addLinkForm.caseNumber) return;
    if (links.find((l) => l.platform === addLinkForm.platform && l.caseNumber === addLinkForm.caseNumber.trim())) {
      toast.error(t('dashboard.marketer.projects.dialogs.duplicateLink'));
      return;
    }
    setIsSubmitting(true);
    try {
      const newLinkId = await addLink({
        platform: addLinkForm.platform,
        caseNumber: addLinkForm.caseNumber,
        actualReceived: 0,
        url: addLinkForm.url || undefined,
        treatmentCategoryId: addLinkForm.treatmentCategoryId || undefined,
        linkTitle: addLinkForm.linkTitle || undefined,
        addedAt: new Date(),
        addedBy: currentUser?.uid,
      });
      const amt = addLinkForm.actualReceived ? Number(addLinkForm.actualReceived) : 0;
      if (amt > 0) {
        await addDonation({
          linkId: newLinkId,
          platform: addLinkForm.platform,
          caseNumber: addLinkForm.caseNumber,
          amount: amt,
          date: new Date(),
          source: 'manual',
          // Load-bearing: 'agg_' prefix is what findAggregateDonation matches on — never rename.
          importKey: 'agg_' + newLinkId,
        });
        await refreshLinkTotals();
      }
      setShowAddLink(false);
      resetAddLinkForm();
      toast.success(t('dashboard.marketer.projects.dialogs.projectAdded'));
    } catch {
      toast.error(t('dashboard.marketer.projects.dialogs.projectError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveEditProject = async () => {
    if (!editingLink || scopedLoading) return;
    const linkDonations = scopedDonations;
    const isDerivedFromJood = linkDonations.some((d) => d.source === 'jood');
    const aggDonation = findAggregateDonation(linkDonations);
    const newReceived = editLinkForm.actualReceived ? Number(editLinkForm.actualReceived) : 0;
    if (!isDerivedFromJood && !canReduceReceived(newReceived, editingLink.totalAllocated, marketingX, marketingY)) {
      toast.error(t('dashboard.marketer.projects.dialogs.cannotReduce'));
      return;
    }
    // CRITICAL fix: the "received" field is prefilled from the COMPUTED total (sum of every
    // donation on the link), so the write must target that same total — never the aggregate
    // donation alone — or itemised donations get double-counted. See computeAggregateDonationWrite.
    const nonAggregateTotal = linkDonations
      .filter((d) => d.id !== aggDonation?.id)
      .reduce((s, d) => s + (d.amount || 0), 0);
    const writeDecision = isDerivedFromJood
      ? null
      : computeAggregateDonationWrite(newReceived, editingLink.actualReceived, aggDonation, nonAggregateTotal);
    if (writeDecision?.op === 'refuse') {
      toast.error(t('dashboard.marketer.projects.dialogs.receivedBelowItemised'));
      return;
    }
    setIsSubmitting(true);
    try {
      await updateLink(editingLink.id, {
        url: editLinkForm.url || undefined,
        treatmentCategoryId: editLinkForm.treatmentCategoryId || undefined,
        linkTitle: editLinkForm.linkTitle || undefined,
      });
      if (writeDecision?.op === 'update' && aggDonation) {
        await updateDonation(aggDonation.id, { amount: writeDecision.amount });
        await refreshLinkTotals();
      } else if (writeDecision?.op === 'create') {
        await addDonation({
          linkId: editingLink.id,
          platform: editingLink.platform as 'jood' | 'ehsan' | 'tabarru' | 'shefaa',
          caseNumber: editingLink.platformCaseNumber,
          amount: writeDecision.amount,
          date: new Date(),
          source: 'manual',
          importKey: 'agg_' + editingLink.id,
        });
        await refreshLinkTotals();
      }
      setEditingLink(null);
      toast.success(t('dashboard.marketer.projects.dialogs.projectUpdated'));
    } catch {
      toast.error(t('dashboard.marketer.projects.dialogs.projectError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteProject = async (link: ComputedLink) => {
    const linkedCases = requests.filter((r) => r.donationEntries?.some((e) => e.linkId === link.id));
    const msg =
      linkedCases.length > 0
        ? t('dashboard.marketer.projects.dialogs.confirmDeleteLinked', { count: linkedCases.length })
        : t('dashboard.marketer.projects.dialogs.confirmDelete');
    if (!confirm(msg)) return;
    setIsSubmitting(true);
    try {
      for (const req of linkedCases) {
        const updatedEntries = (req.donationEntries || []).filter((e) => e.linkId !== link.id);
        await updateRequest(req.id, { donationEntries: updatedEntries, updatedAt: new Date() });
      }
      // Deliberately do NOT delete the project's `donations` documents — matches the
      // coordinator's delete, which leaves them too.
      await deleteLink(link.id);
      toast.success(t('dashboard.marketer.projects.dialogs.projectDeleted'));
    } catch {
      toast.error(t('dashboard.marketer.projects.dialogs.projectError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSaveDonation = async () => {
    if (!managingDonationsLink) return;
    const amount = Number(donationForm.amount);
    if (!donationForm.amount || isNaN(amount) || amount <= 0 || !donationForm.date) {
      toast.error(t('dashboard.marketer.projects.dialogs.donationError'));
      return;
    }
    setIsSubmitting(true);
    try {
      const date = noonLocalDate(donationForm.date);
      if (donationForm.id) {
        await updateDonation(donationForm.id, {
          amount,
          date,
          donorName: donationForm.donorName || undefined,
          donorPhone: donationForm.donorPhone || undefined,
          paymentMethod: donationForm.paymentMethod || undefined,
        });
      } else {
        await addDonation({
          linkId: managingDonationsLink.id,
          platform: managingDonationsLink.platform as 'jood' | 'ehsan' | 'tabarru' | 'shefaa',
          caseNumber: managingDonationsLink.platformCaseNumber,
          amount,
          date,
          donorName: donationForm.donorName || undefined,
          donorPhone: donationForm.donorPhone || undefined,
          paymentMethod: donationForm.paymentMethod || undefined,
          source: 'manual',
          importKey: `man_${managingDonationsLink.id}_${Date.now()}`,
        });
      }
      await Promise.all([refreshScopedDonations(), refreshLinkTotals()]);
      setShowDonationForm(false);
      setDonationForm(emptyDonationForm);
      toast.success(t('dashboard.marketer.projects.dialogs.donationUpdated'));
    } catch {
      toast.error(t('dashboard.marketer.projects.dialogs.donationError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleDeleteDonation = async (d: DonationTransaction) => {
    if (!confirm(t('dashboard.marketer.projects.dialogs.confirmDeleteDonation'))) return;
    setIsSubmitting(true);
    try {
      await deleteDonation(d.id);
      await Promise.all([refreshScopedDonations(), refreshLinkTotals()]);
      toast.success(t('dashboard.marketer.projects.dialogs.donationRemoved'));
    } catch {
      toast.error(t('dashboard.marketer.projects.dialogs.donationError'));
    } finally {
      setIsSubmitting(false);
    }
  };

  // ===== Render pieces =====

  const addButtonNode = (
    <Button size="sm" onClick={() => { resetAddLinkForm(); setShowAddLink(true); }}>
      <Plus className="h-4 w-4 me-2" />
      {t('dashboard.marketer.projects.dialogs.addProject')}
    </Button>
  );

  const renderRowMenu = (link: ComputedLink) => (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="ghost" size="icon">
          <MoreHorizontal className="h-4 w-4" />
        </Button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end">
        <DropdownMenuItem onClick={() => startEditLink(link)}>
          <Pencil className="h-4 w-4 me-2" />
          {t('common.edit')}
        </DropdownMenuItem>
        <DropdownMenuItem onClick={() => { setManagingDonationsLink(link); setShowDonationForm(false); }}>
          <SaudiRiyalIcon className="h-4 w-4 me-2" />
          {t('dashboard.marketer.projects.dialogs.manageDonations')}
        </DropdownMenuItem>
        <DropdownMenuSeparator />
        <DropdownMenuItem className="text-destructive" onClick={() => handleDeleteProject(link)}>
          <Trash2 className="h-4 w-4 me-2" />
          {t('common.delete')}
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );

  // `scopedDonations` is already fetched scoped to whichever link's dialog is currently open
  // (scopedLinkId) — this just guards against a stale linkId that doesn't match it.
  const linkDonationsFor = (linkId: string) => (linkId === scopedLinkId ? scopedDonations : []);

  const dialogsNode = (
    <>
      {/* Add Project Dialog */}
      <Dialog open={showAddLink} onOpenChange={(o) => { if (!o) setShowAddLink(false); }}>
        <DialogContent className="max-w-md" dir={dir}>
          <DialogHeader>
            <DialogTitle>{t('dashboard.marketer.projects.dialogs.addProject')}</DialogTitle>
            <DialogDescription className="sr-only">{t('dashboard.marketer.projects.dialogs.addProject')}</DialogDescription>
          </DialogHeader>
          <div className="space-y-3 py-2">
            <div className="space-y-1">
              <Label>{t('dashboard.marketer.projects.dialogs.platform')} *</Label>
              <select
                value={addLinkForm.platform}
                onChange={(e) => setAddLinkForm((f) => ({ ...f, platform: e.target.value as typeof f.platform }))}
                className="w-full h-10 px-3 rounded-md border border-input bg-background text-sm"
                dir={dir}
              >
                <option value="">—</option>
                <option value="jood">{t('dashboard.coordinator.platforms.jood')}</option>
                <option value="ehsan">{t('dashboard.coordinator.platforms.ehsan')}</option>
                <option value="tabarru">{t('dashboard.coordinator.platforms.tabarru')}</option>
                <option value="shefaa">{t('dashboard.coordinator.platforms.shefaa')}</option>
              </select>
            </div>
            <div className="space-y-1">
              <Label>{t('dashboard.marketer.projects.dialogs.platformCaseNumber')} *</Label>
              <Input value={addLinkForm.caseNumber} onChange={(e) => setAddLinkForm((f) => ({ ...f, caseNumber: e.target.value }))} dir="ltr" />
            </div>
            <div className="space-y-1">
              <Label>{t('dashboard.marketer.projects.dialogs.linkTitle')}</Label>
              <Input value={addLinkForm.linkTitle} onChange={(e) => setAddLinkForm((f) => ({ ...f, linkTitle: e.target.value }))} />
            </div>
            <div className="space-y-1">
              <Label>{t('dashboard.marketer.projects.dialogs.url')}</Label>
              <Input value={addLinkForm.url} onChange={(e) => setAddLinkForm((f) => ({ ...f, url: e.target.value }))} dir="ltr" />
            </div>
            <div className="space-y-1">
              <Label>{t('dashboard.marketer.projects.dialogs.treatmentCategory')}</Label>
              <CategorySelect
                value={addLinkForm.treatmentCategoryId || undefined}
                activeCategories={activeCategories}
                onChange={(id) => setAddLinkForm((f) => ({ ...f, treatmentCategoryId: id || '' }))}
                placeholderKey="dashboard.marketer.projects.dialogs.treatmentCategoryPlaceholder"
                clearLabelKey="dashboard.marketer.projects.dialogs.treatmentCategoryClear"
                triggerClassName="h-10 w-full"
              />
            </div>
            <div className="space-y-1">
              <Label>{t('dashboard.marketer.projects.dialogs.received')}</Label>
              <Input type="number" value={addLinkForm.actualReceived} onChange={(e) => setAddLinkForm((f) => ({ ...f, actualReceived: e.target.value }))} dir="ltr" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAddLink(false)}>{t('common.cancel')}</Button>
            <Button disabled={isSubmitting || !addLinkForm.platform || !addLinkForm.caseNumber} onClick={handleAddProject}>
              {isSubmitting && <Loader2 className="h-4 w-4 me-2 animate-spin" />}{t('common.save')}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Project Dialog */}
      <Dialog open={!!editingLink} onOpenChange={(o) => { if (!o) setEditingLink(null); }}>
        <DialogContent className="max-w-md" dir={dir}>
          <DialogHeader>
            <DialogTitle>{t('dashboard.marketer.projects.dialogs.editProject')}</DialogTitle>
            <DialogDescription className="sr-only">{t('dashboard.marketer.projects.dialogs.editProject')}</DialogDescription>
          </DialogHeader>
          {editingLink && (
            <>
              <div className="flex items-center gap-2 text-sm text-muted-foreground pb-1">
                <Badge variant="outline">{t(`dashboard.coordinator.platforms.${editingLink.platform}`)}</Badge>
                <span className="font-mono" dir="ltr">{editingLink.platformCaseNumber}</span>
              </div>
              <div className="space-y-3 py-2">
                <div className="space-y-1">
                  <Label>{t('dashboard.marketer.projects.dialogs.linkTitle')}</Label>
                  <Input value={editLinkForm.linkTitle} onChange={(e) => setEditLinkForm((f) => ({ ...f, linkTitle: e.target.value }))} />
                </div>
                <div className="space-y-1">
                  <Label>{t('dashboard.marketer.projects.dialogs.url')}</Label>
                  <Input value={editLinkForm.url} onChange={(e) => setEditLinkForm((f) => ({ ...f, url: e.target.value }))} dir="ltr" />
                </div>
                <div className="space-y-1">
                  <Label>{t('dashboard.marketer.projects.dialogs.treatmentCategory')}</Label>
                  <CategorySelect
                    value={editLinkForm.treatmentCategoryId || undefined}
                    activeCategories={activeCategories}
                    onChange={(id) => setEditLinkForm((f) => ({ ...f, treatmentCategoryId: id || '' }))}
                    placeholderKey="dashboard.marketer.projects.dialogs.treatmentCategoryPlaceholder"
                    clearLabelKey="dashboard.marketer.projects.dialogs.treatmentCategoryClear"
                    triggerClassName="h-10 w-full"
                  />
                </div>
                <div className="space-y-1">
                  <Label>{t('dashboard.marketer.projects.dialogs.received')}</Label>
                  <Input
                    type="number"
                    value={editLinkForm.actualReceived}
                    onChange={(e) => setEditLinkForm((f) => ({ ...f, actualReceived: e.target.value }))}
                    dir="ltr"
                    readOnly={linkDonationsFor(editingLink.id).some((d) => d.source === 'jood')}
                    disabled={linkDonationsFor(editingLink.id).some((d) => d.source === 'jood')}
                  />
                  {linkDonationsFor(editingLink.id).some((d) => d.source === 'jood') && (
                    <p className="text-xs text-muted-foreground">{t('dashboard.marketer.projects.dialogs.receivedLockedJood')}</p>
                  )}
                </div>
              </div>
              <DialogFooter>
                <Button variant="outline" onClick={() => setEditingLink(null)}>{t('common.cancel')}</Button>
                <Button disabled={isSubmitting || scopedLoading} onClick={handleSaveEditProject}>
                  {(isSubmitting || scopedLoading) && <Loader2 className="h-4 w-4 me-2 animate-spin" />}{t('common.save')}
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Manage Donations Dialog */}
      <Dialog
        open={!!managingDonationsLink}
        onOpenChange={(open) => {
          if (!open) {
            setManagingDonationsLink(null);
            setShowDonationForm(false);
            setDonationForm(emptyDonationForm);
          }
        }}
      >
        <DialogContent className="max-w-2xl max-h-[85vh] overflow-y-auto" dir={dir}>
          <DialogHeader>
            <DialogTitle>
              {t('dashboard.marketer.projects.dialogs.manageDonations')}
              {managingDonationsLink && (
                <span className="block text-sm font-normal text-muted-foreground mt-1">
                  {managingDonationsLink.linkTitle || managingDonationsLink.platformCaseNumber} ·{' '}
                  {/* I3 fix: compute live from this link's donations, not the actualReceived snapshot
                      taken when the row menu was clicked — that snapshot goes stale as donations
                      are added/removed while this dialog is open. */}
                  {Math.round(linkDonationsFor(managingDonationsLink.id).reduce((s, d) => s + (d.amount || 0), 0)).toLocaleString('en-US')} {t('common.sar')}
                  {' · '}{linkDonationsFor(managingDonationsLink.id).length} {t('dashboard.marketer.projects.dialogs.donationsCount')}
                </span>
              )}
            </DialogTitle>
            <DialogDescription className="sr-only">{t('dashboard.marketer.projects.dialogs.manageDonations')}</DialogDescription>
          </DialogHeader>

          {managingDonationsLink && (
            <div className="space-y-4">
              {!showDonationForm && (
                <Button size="sm" onClick={startAddDonation} disabled={isSubmitting}>
                  <Plus className="h-4 w-4 me-2" />{t('dashboard.marketer.projects.dialogs.addDonation')}
                </Button>
              )}
              {showDonationForm && (
                <div className="border rounded-md p-4 space-y-3 bg-muted/30">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                    <div className="space-y-1">
                      <Label>{t('dashboard.marketer.projects.dialogs.donationAmount')} *</Label>
                      <Input type="number" value={donationForm.amount} dir="ltr" onChange={(e) => setDonationForm((f) => ({ ...f, amount: e.target.value }))} />
                    </div>
                    <div className="space-y-1">
                      <Label>{t('dashboard.marketer.projects.dialogs.donationDate')} *</Label>
                      <Input type="date" value={donationForm.date} dir="ltr" onChange={(e) => setDonationForm((f) => ({ ...f, date: e.target.value }))} />
                    </div>
                    <div className="space-y-1">
                      <Label>{t('dashboard.marketer.projects.dialogs.donorName')}</Label>
                      <Input value={donationForm.donorName} onChange={(e) => setDonationForm((f) => ({ ...f, donorName: e.target.value }))} />
                    </div>
                    <div className="space-y-1">
                      <Label>{t('dashboard.marketer.projects.dialogs.donorPhone')}</Label>
                      <Input value={donationForm.donorPhone} dir="ltr" onChange={(e) => setDonationForm((f) => ({ ...f, donorPhone: e.target.value }))} />
                    </div>
                    <div className="space-y-1 sm:col-span-2">
                      <Label>{t('dashboard.marketer.projects.dialogs.paymentMethod')}</Label>
                      <Input value={donationForm.paymentMethod} onChange={(e) => setDonationForm((f) => ({ ...f, paymentMethod: e.target.value }))} />
                    </div>
                  </div>
                  <div className="flex gap-2 justify-end">
                    <Button variant="outline" size="sm" onClick={() => { setShowDonationForm(false); setDonationForm(emptyDonationForm); }}>{t('common.cancel')}</Button>
                    <Button size="sm" onClick={handleSaveDonation} disabled={isSubmitting}>
                      {isSubmitting ? <Loader2 className="h-4 w-4 me-2 animate-spin" /> : null}{t('common.save')}
                    </Button>
                  </div>
                </div>
              )}
              <div className="border rounded-md max-h-[45vh] overflow-y-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead className="text-start">{t('dashboard.marketer.projects.dialogs.donationDate')}</TableHead>
                      <TableHead className="text-start">{t('dashboard.marketer.projects.dialogs.donationAmount')}</TableHead>
                      <TableHead className="text-start">{t('dashboard.marketer.projects.dialogs.donorName')}</TableHead>
                      <TableHead className="text-start">{t('dashboard.marketer.projects.dialogs.source')}</TableHead>
                      <TableHead className="text-end">{t('dashboard.marketer.projects.dialogs.donationActions')}</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {linkDonationsFor(managingDonationsLink.id).length === 0 ? (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-muted-foreground py-6">
                          {t('dashboard.marketer.projects.dialogs.noDonations')}
                        </TableCell>
                      </TableRow>
                    ) : (
                      linkDonationsFor(managingDonationsLink.id).map((d) => (
                        <TableRow key={d.id}>
                          <TableCell className="whitespace-nowrap" dir="ltr">{formatDonationDate(d.date)}</TableCell>
                          <TableCell>
                            <span className="inline-flex items-center gap-1">{(d.amount || 0).toLocaleString('en-US')} <SaudiRiyalIcon className="h-3.5 w-3.5" /></span>
                          </TableCell>
                          <TableCell>{d.donorName || '-'}</TableCell>
                          <TableCell>
                            <Badge variant={d.source === 'jood' ? 'secondary' : 'outline'} className="text-xs">
                              {t(`dashboard.marketer.projects.dialogs.donationSource.${d.source}`)}
                            </Badge>
                          </TableCell>
                          <TableCell className="text-end">
                            <div className="flex gap-1 justify-end">
                              <Button variant="ghost" size="icon" className="h-7 w-7" disabled={isSubmitting} onClick={() => startEditDonation(d)}>
                                <Pencil className="h-3.5 w-3.5" />
                              </Button>
                              <Button variant="ghost" size="icon" className="h-7 w-7 text-destructive" disabled={isSubmitting} onClick={() => handleDeleteDonation(d)}>
                                <Trash2 className="h-3.5 w-3.5" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))
                    )}
                  </TableBody>
                </Table>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </>
  );

  return { addButtonNode, renderRowMenu, dialogsNode };
}
